/**
 * use-jobs — TanStack Query hook for the Job Queue.
 *
 * Phase 9 / Plan 09-04 — polls GET /v1/jobs every 30 seconds (DISPATCH-01).
 * Filter state is passed via the `filters` param; the caller (JobQueue) reads
 * them from URL search params and passes them here.
 *
 * CRITICAL: array query key prevents cache collisions with other feature hooks
 * (RESEARCH Pitfall 4). The `refetchIntervalInBackground: false` flag pauses
 * polling when the tab is hidden — T-09-04-05 (accept DoS self posture).
 *
 * Spec: PATTERNS.md use-jobs.ts section; RESEARCH Pitfall 4; T-09-04-05.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import { JOBS_LIST_MAX_LIMIT } from '@lodex-pro/shared/jobs-paging';

/** Filter parameters for GET /v1/jobs. All optional. */
export interface JobFilters {
  /** Comma-separated list of status codes to include (e.g. "PENDING,REJECTED"). */
  readonly status?: string;
  /**
   * Unified search across driver name, pickup site, requestor and haul id
   * (ClickUp 86bazkpgy).
   *
   * Replaces a `driverId` this type used to declare and the Job Board used to send. The
   * server never accepted it — `listJobsQuery` had no such field, so Zod stripped it and the
   * board filtered on nothing. It could not have worked either way: the input is a free-text
   * NAME and `driverId` was a uuid.
   */
  readonly q?: string;
  /** Filter by paused state. */
  readonly paused?: boolean;
  /** Maximum number of jobs to return. */
  readonly limit?: number;
  /** Offset for pagination. */
  readonly offset?: number;
}

/** A single job row as returned by GET /v1/jobs. */
export interface DispatchJob {
  readonly id: string;
  // Null until the Haul ID (ticket number) is issued — PENDING jobs have none (#4).
  readonly haulId: string | null;
  readonly statusCode: string;
  readonly statusLabel: string;
  readonly jobTypeCode: string;
  readonly jobTypeLabel: string;
  readonly scheduledAt: string | null;
  readonly completedAt: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly siteName: string;
  readonly apiNumber: string | null;
  readonly operatorName: string;
  readonly driverName: string | null;
  readonly driverId: string | null;
  // Wave 3 Part 2 — dispatch-board intra-column priority order (nullable until set).
  readonly dispatcherSortOrder: number | null;
  // Quick 260617-map (#4) — total captured BBL; NULL when no capture exists yet
  // (the chip shows nothing). A capture with values shows the total. Optional so
  // existing DispatchJob literals (tests) compile without churn; the API always sends it.
  readonly capturedBbl?: number | null;
  // 260624-job-destination — disposal facility name (COALESCE actual, planned).
  // For an active/PENDING job this is the dispatcher's planned destination.
  // Optional so existing DispatchJob literals (tests) compile without churn.
  readonly disposalFacilityName?: string | null;
  // Quick 260721 — Job Board flat table. `createdByName` backs the Requestor
  // column (jobs.created_by_id → creator.full_name); `dispatcherNotes` backs the
  // inline-editable notes cell. Optional so existing literals compile without churn.
  readonly createdByName?: string | null;
  /**
   * 86bazkeyn — the REQUESTOR: the customer contact who called this load in.
   *
   * Not `createdByName`, which is the dispatcher who keyed the ticket in. The two were
   * conflated under one "Requestor" column until OPEN-053 renamed that one to "Created by";
   * this is the column that actually means what the label says.
   *
   * Null for tickets typed with the free-text requester fields, or created before the
   * requestor table existed.
   */
  readonly requestorName?: string | null;
  readonly requestorId?: string | null;
  readonly dispatcherNotes?: string | null;
  /**
   * DEC-299 / Task 18.6 — the OPERATOR's note on a haul request made through the public
   * API (jobs.operator_request_notes), verbatim. Never merged into dispatcherNotes: it is
   * rendered by OperatorRequestNote, labelled as the operator's words. Null for every job a
   * dispatcher, driver or foreman created. Optional so existing literals compile.
   */
  readonly operatorRequestNotes?: string | null;
  // Quick 260722 — Site detail popup routing. A hauler_location pickup exposes
  // its id as `siteId` (= hauler_location_id); a standalone-well pickup has
  // siteId NULL and `pickupWellId` set. The Site link opens the matching detail.
  readonly siteId?: string | null;
  readonly pickupWellId?: string | null;
  // DEC-146 (Phase 17) — urgent flag drives the amber URGENT badge.
  readonly isUrgent?: boolean;
  // The job's timeclock chain head (STARTED | RESUMED | PAUSED | …), null with no clock.
  // #46 / DEC-361 — STARTED or RESUMED means the job is GOING (pinned first).
  readonly clockState?: string | null;
  // OPEN-116 — a driver-created load still not started after the company's unsynced-alert
  // threshold (default 4 h). The board flags it for a dispatcher to cancel or nudge.
  readonly notStarted?: boolean;
}

/** Response envelope for GET /v1/jobs. */
export interface JobsResponse {
  readonly jobs: readonly DispatchJob[];
}

/**
 * Stable array query key for the jobs list. Includes filters so each unique
 * filter set gets its own cache entry (RESEARCH Pitfall 4).
 */
export const JOBS_QUERY_KEY = (filters: JobFilters): readonly [string, JobFilters] =>
  ['jobs', filters] as const;

/**
 * Build a URL query-string from a `JobFilters` object (omits undefined/null values).
 *
 * Exported for test: the Job Board's search shipped broken for months because the client sent
 * a param (`driverId`) that the server's schema did not declare, so Zod silently dropped it.
 * Nothing on either side failed loudly. Pinning the emitted query string is the cheapest
 * guard against a filter that looks wired but reaches nothing.
 */
export function buildQS(filters: JobFilters): string {
  const params = new URLSearchParams();
  if (filters.status !== undefined && filters.status.length > 0) {
    params.set('status', filters.status);
  }
  if (filters.q !== undefined && filters.q.length > 0) {
    params.set('q', filters.q);
  }
  if (filters.paused !== undefined) {
    params.set('paused', String(filters.paused));
  }
  if (filters.limit !== undefined) {
    params.set('limit', String(filters.limit));
  }
  if (filters.offset !== undefined) {
    params.set('offset', String(filters.offset));
  }
  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

/**
 * Safety stop for {@link fetchAllJobs}: 50 pages of JOBS_LIST_MAX_LIMIT = 5,000 open jobs, far
 * above any real tenant. It exists only so a server that ignored `offset` (returning the same
 * full page forever) cannot spin every poll into an endless loop.
 */
const MAX_PAGES = 50;

async function fetchJobsPage(
  apiOpts: ReturnType<typeof useApiOpts>,
  path: string,
): Promise<JobsResponse> {
  const res = await authedFetch(apiOpts, path);
  if (!res.ok) {
    // Same error useApiQuery throws, so callers branching on ApiError keep working.
    throw await apiErrorFromResponse(res, path);
  }
  return res.json() as Promise<JobsResponse>;
}

/**
 * OPEN-143 — read EVERY page of GET /v1/jobs for the given filters.
 *
 * The server defaults `limit` to 50, and every caller that wants "all open jobs" (the dispatch
 * board, DriverStack, the Operations header, the Job Queue) sent no limit at all — so each one
 * silently saw only the first 50 rows of the board sort. That sort is global on
 * dispatcher_sort_order, which the assignment endpoint renumbers per driver column (0,1,2…).
 * Dropping a job at the end of a busy column gave it a high number and pushed it past row 50:
 * the optimistic patch drew it, the settle refetch erased it, and every 30s poll kept it erased
 * until some other move renumbered the columns and pulled it back onto the page.
 *
 * Rows can shift between page requests while dispatchers work, so pages are deduplicated by id;
 * the next poll corrects anything a shift skipped.
 */
export async function fetchAllJobs(
  apiOpts: ReturnType<typeof useApiOpts>,
  filters: JobFilters,
): Promise<JobsResponse> {
  const seen = new Set<string>();
  const jobs: DispatchJob[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const qs = buildQS({
      ...filters,
      limit: JOBS_LIST_MAX_LIMIT,
      offset: page * JOBS_LIST_MAX_LIMIT,
    });
    const body = await fetchJobsPage(apiOpts, `/v1/jobs${qs}`);
    for (const j of body.jobs) {
      if (seen.has(j.id)) continue;
      seen.add(j.id);
      jobs.push(j);
    }
    if (body.jobs.length < JOBS_LIST_MAX_LIMIT) break;
  }
  return { jobs };
}

/**
 * Polls the job queue every 30 seconds. Returns `{ data, isLoading, isFetching, isError }`.
 * Use `isFetching` (not `isLoading`) for the "Refreshing…" indicator after first load.
 *
 * Pauses when the tab is hidden (refetchIntervalInBackground: false) — T-09-04-05.
 *
 * Without `filters.limit` it returns ALL matching jobs, every page (OPEN-143). With a limit it
 * returns exactly that one page.
 *
 * @param filters Filter parameters; caller should memoize or keep stable to avoid
 *   unnecessary cache misses. JobQueue uses URLSearchParams values which are stable
 *   across renders unless the user changes filters.
 */
export function useJobs(
  apiOpts: ReturnType<typeof useApiOpts>,
  filters: JobFilters,
): UseQueryResult<JobsResponse> {
  return useQuery<JobsResponse>({
    queryKey: JOBS_QUERY_KEY(filters),
    queryFn: (): Promise<JobsResponse> =>
      filters.limit !== undefined
        ? fetchJobsPage(apiOpts, `/v1/jobs${buildQS(filters)}`)
        : fetchAllJobs(apiOpts, filters),
    refetchInterval: 30_000,
    staleTime: 25_000,
    refetchIntervalInBackground: false,
  });
}
